import { useEffect, useState } from "react";
import DashboardLayout from "@/components/DashboardLayout";
import api, { apiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Switch } from "@/components/ui/switch";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import { Plus, UserCog, Power, FileSpreadsheet } from "lucide-react";
import { BulkImportDialog } from "@/components/BulkImportDialog";

const ROLES = [["warehouse_agent","Warehouse Agent"],["front_desk_agent","Front-Desk Agent"],["supervisor","Supervisor"],["company_admin","Company Admin"]];
const EMPTY = { full_name: "", email: "", password: "", employee_number: "", role: "warehouse_agent", primary_store_id: "", store_ids: [], is_active: true };

export default function Employees() {
  const [users, setUsers] = useState([]);
  const [stores, setStores] = useState([]);
  const [form, setForm] = useState(null);
  const [editing, setEditing] = useState(null);
  const [importOpen, setImportOpen] = useState(false);

  const load = () => {
    api.get("/users").then((r) => setUsers(r.data.users)).catch(() => {});
    api.get("/stores").then((r) => setStores(r.data.stores)).catch(() => {});
  };
  useEffect(load, []);

  const save = async () => {
    try {
      const payload = { ...form, primary_store_id: form.primary_store_id || (form.store_ids[0] || null) };
      if (editing) await api.put(`/users/${editing}`, payload);
      else await api.post("/users", payload);
      toast.success("Employee saved"); setForm(null); setEditing(null); load();
    } catch (e) { toast.error(apiError(e)); }
  };
  const toggle = async (u) => {
    try { await api.post(`/users/${u.id}/toggle`); load(); } catch (e) { toast.error(apiError(e)); }
  };
  const toggleStore = (id) => {
    setForm((f) => {
      const has = f.store_ids.includes(id);
      return { ...f, store_ids: has ? f.store_ids.filter((x) => x !== id) : [...f.store_ids, id] };
    });
  };

  return (
    <DashboardLayout title="Employees" needsStore={false}>
      <div className="flex justify-end gap-2 mb-4">
        <Button variant="outline" className="gap-2" onClick={() => setImportOpen(true)} data-testid="import-employees-btn"><FileSpreadsheet size={16} /> Import from Spreadsheet</Button>
        <BulkImportDialog open={importOpen} onOpenChange={setImportOpen} onDone={load} />
        <Button className="gap-2" onClick={() => { setForm({ ...EMPTY }); setEditing(null); }} data-testid="add-employee-btn"><Plus size={16} /> Add Employee</Button>
      </div>
      <div className="bg-card border border-border overflow-x-auto">
        <table className="w-full text-sm" data-testid="employees-table">
          <thead>
            <tr className="text-left text-xs uppercase tracking-wide text-muted-foreground border-b border-border">
              <th className="px-3 py-2">Name</th><th className="px-3 py-2">Emp #</th><th className="px-3 py-2">Role</th>
              <th className="px-3 py-2">Stores</th><th className="px-3 py-2">Status</th><th className="px-3 py-2">Actions</th>
            </tr>
          </thead>
          <tbody>
            {users.map((u) => (
              <tr key={u.id} className="border-b border-border hover:bg-secondary/40 transition-colors" data-testid={`emp-row-${u.email}`}>
                <td className="px-3 py-3"><div className="font-semibold">{u.full_name}</div><div className="text-xs text-muted-foreground">{u.email}</div></td>
                <td className="px-3 py-3 font-mono-t text-xs">{u.employee_number}</td>
                <td className="px-3 py-3"><Badge variant="outline" className="uppercase text-[10px]">{u.role.replace(/_/g," ")}</Badge></td>
                <td className="px-3 py-3 text-xs">{(u.store_ids||[]).map((id) => stores.find((s)=>s.id===id)?.store_code).filter(Boolean).join(", ") || "—"}</td>
                <td className="px-3 py-3">{u.is_active ? <span className="text-alert-green text-xs">Active</span> : <span className="text-muted-foreground text-xs">Inactive</span>}</td>
                <td className="px-3 py-3">
                  <div className="flex gap-2">
                    <Button size="sm" variant="outline" className="h-8 gap-1" onClick={() => { setForm({ ...u, password: "" }); setEditing(u.id); }} data-testid={`edit-emp-${u.email}`}><UserCog size={13} /></Button>
                    {u.role !== "platform_admin" && <Button size="sm" variant="outline" className="h-8 gap-1" onClick={() => toggle(u)}><Power size={13} /></Button>}
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <Dialog open={!!form} onOpenChange={(o) => !o && setForm(null)}>
        <DialogContent className="bg-card border-border max-w-lg">
          <DialogHeader><DialogTitle className="font-display text-2xl">{editing ? "Edit" : "Add"} Employee</DialogTitle></DialogHeader>
          {form && (
            <div className="space-y-3 py-2">
              <div className="grid grid-cols-2 gap-3">
                <div><label className="text-xs uppercase text-muted-foreground">Full Name</label>
                  <Input className="mt-1" value={form.full_name} onChange={(e)=>setForm({...form,full_name:e.target.value})} data-testid="emp-name" /></div>
                <div><label className="text-xs uppercase text-muted-foreground">Employee #</label>
                  <Input className="mt-1" value={form.employee_number} onChange={(e)=>setForm({...form,employee_number:e.target.value})} /></div>
              </div>
              <div><label className="text-xs uppercase text-muted-foreground">Email</label>
                <Input className="mt-1" type="email" value={form.email} onChange={(e)=>setForm({...form,email:e.target.value})} data-testid="emp-email" /></div>
              <div><label className="text-xs uppercase text-muted-foreground">Password {editing && "(leave blank to keep)"}</label>
                <Input className="mt-1" type="text" value={form.password} onChange={(e)=>setForm({...form,password:e.target.value})} placeholder={editing ? "••••••" : "Welcome123"} data-testid="emp-password" /></div>
              <div><label className="text-xs uppercase text-muted-foreground">Role</label>
                <Select value={form.role} onValueChange={(v)=>setForm({...form,role:v})}>
                  <SelectTrigger className="mt-1" data-testid="emp-role"><SelectValue /></SelectTrigger>
                  <SelectContent>{ROLES.map(([v,l])=><SelectItem key={v} value={v}>{l}</SelectItem>)}</SelectContent>
                </Select></div>
              <div>
                <label className="text-xs uppercase text-muted-foreground">Assigned Stores</label>
                <div className="grid grid-cols-2 gap-2 mt-1">
                  {stores.map((s) => (
                    <label key={s.id} className={`flex items-center gap-2 p-2 border cursor-pointer text-sm ${form.store_ids.includes(s.id) ? "border-white bg-secondary" : "border-border"}`}>
                      <input type="checkbox" checked={form.store_ids.includes(s.id)} onChange={() => toggleStore(s.id)} data-testid={`emp-store-${s.store_code}`} />
                      {s.store_name}
                    </label>
                  ))}
                </div>
              </div>
              <div className="flex items-center gap-2"><Switch checked={form.is_active} onCheckedChange={(v)=>setForm({...form,is_active:v})} /><span className="text-sm">Active</span></div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setForm(null)}>Cancel</Button>
            <Button onClick={save} data-testid="save-emp-btn">Save Employee</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </DashboardLayout>
  );
}
